import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { SiteConfigService } from '../../core/site-config.service';

export interface FormUpload {
  uploadID: number;
  commitToken?: string;
  fileName: string;
  tempFile: string;
  mimeType: string;
  isImage: boolean;
}

@Injectable({ providedIn: 'root' })
export class FormUploadService {
  private readonly api = inject(ApiClientService);
  private readonly site = inject(SiteConfigService);

  async upload(fieldKey: string, file: File, endpoint = '/upload/form'): Promise<FormUpload> {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append('fieldKey', fieldKey);
    body.append('lastModified', String(file.lastModified || 0));
    const response = await firstValueFrom(this.api.postFormData<unknown>(endpoint, body));
    const result = this.findUpload(response);
    if (!result?.['uploadID']) {
      throw new Error('Dosya yüklenemedi.');
    }
    const previewUrl = this.normalizePreviewUrl(String(result['publicURL'] || result['tempFile'] || ''));
    return {
      uploadID: Number(result['uploadID']),
      commitToken: String(result['commitToken'] || ''),
      fileName: String(result['fileName'] || file.name),
      // Keep the server URL in the draft. A blob: URL only belongs to the
      // current document and becomes invalid after a page refresh.
      tempFile: previewUrl,
      mimeType: String(result['mimeType'] || file.type),
      isImage: result['isImage'] !== undefined ? !!result['isImage'] : file.type.startsWith('image/')
    };
  }

  private normalizePreviewUrl(value: string): string {
    const raw=String(value || '').trim().replace(/\[size\]/gi, 'b');
    if (!raw) return '';
    const origin=this.site.config().apiBaseUrl.replace(/\/+$/,'');
    // Older upload responses used `https://temporaryfiles/...` as a pseudo
    // host. It is still a temporary-file URL; do not strip that path or the
    // browser will request `/forms/...` instead of `/temporaryFiles/forms/...`.
    if (/^https?:\/\/\/?temporaryfiles(?:\/|$)/i.test(raw)) return `${origin}/temporaryFiles/${raw.replace(/^https?:\/\/\/?temporaryfiles\/?/i,'')}`;
    if (/^temporaryfiles(?:\/|$)/i.test(raw)) return `${origin}/temporaryFiles/${raw.replace(/^temporaryfiles\/?/i,'')}`;
    if (/^\/?temporaryfiles(?:\/|$)/i.test(raw)) return `${origin}/temporaryFiles/${raw.replace(/^\/?temporaryfiles\/?/i,'')}`;
    if (/^\/?media(?:\/|$)/i.test(raw)) return `${origin}/${raw.replace(/^\/?/,'')}`;
    return raw;
  }

  private findUpload(response: unknown): Record<string, unknown> | null {
    const stack = Array.isArray(response) ? [...response] : [response];
    while (stack.length) {
      const item = stack.shift();
      if (!item || typeof item !== 'object') {
        continue;
      }
      const record = item as Record<string, unknown>;
      for (const key of ['formUpload', 'mediaUpload']) {
        if (record[key] && typeof record[key] === 'object') {
          return record[key] as Record<string, unknown>;
        }
      }
      for (const value of Object.values(record)) {
        if (value && typeof value === 'object') {
          stack.push(value);
        }
      }
    }
    return null;
  }
}
